/* eslint-disable react-refresh/only-export-components */
import React, { useCallback, useMemo, useRef } from 'react'
import { createEditor, Descendant, Editor, Transforms, Element as SlateElement, Text } from 'slate'
import { Slate, Editable, withReact, RenderElementProps, RenderLeafProps, useSlate, useSelected, useFocused, ReactEditor } from 'slate-react'
import type { MarkFormat, BlockFormat, CustomElement, SlateContent, ImageElement } from '../../types/slate'
import { convex } from '../../lib/convex'
import { api } from '../../convex_generated/api'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const isMarkActive = (editor: Editor, format: MarkFormat): boolean => {
  const marks = Editor.marks(editor)
  return marks ? marks[format] === true : false
}

const toggleMark = (editor: Editor, format: MarkFormat) => {
  if (isMarkActive(editor, format)) {
    Editor.removeMark(editor, format)
  } else {
    Editor.addMark(editor, format, true)
  }
}

const LIST_TYPES: BlockFormat[] = ['bulleted-list']

const isBlockActive = (editor: Editor, format: BlockFormat): boolean => {
  const { selection } = editor
  if (!selection) return false
  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
    }),
  )
  return !!match
}

const toggleBlock = (editor: Editor, format: BlockFormat) => {
  const isActive = isBlockActive(editor, format)
  const isList = LIST_TYPES.includes(format)

  Transforms.unwrapNodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && LIST_TYPES.includes(n.type),
    split: true,
  })

  const newType: CustomElement['type'] = isActive ? 'paragraph' : isList ? 'list-item' : format
  Transforms.setNodes<CustomElement>(editor, { type: newType })

  if (!isActive && isList) {
    const block: CustomElement = { type: format as 'bulleted-list', children: [] }
    Transforms.wrapNodes(editor, block)
  }
}

// Count words across all Slate leaf nodes
export const countWords = (nodes: Descendant[]): number =>
  nodes.reduce((acc, node) => {
    if (Text.isText(node)) return acc + (node.text.match(/\S+/g)?.length ?? 0)
    if ('children' in node) return acc + countWords((node as { children: Descendant[] }).children)
    return acc
  }, 0)

// ---------------------------------------------------------------------------
// withImages plugin — makes image nodes void (non-editable content)
// ---------------------------------------------------------------------------

const withImages = (editor: Editor) => {
  const { isVoid } = editor
  editor.isVoid = (element) =>
    (element as CustomElement).type === 'image' ? true : isVoid(element)
  return editor
}

/** Upload file to Convex storage and insert an image element at the cursor. */
const insertImage = async (editor: Editor, file: File) => {
  // Get upload URL from Convex
  const uploadUrl: string = await convex.mutation(api.branches.generateBranchUploadUrl)
  // Upload file
  const res = await fetch(uploadUrl, {
    method: 'POST',
    headers: { 'Content-Type': file.type },
    body: file,
  })
  if (!res.ok) throw new Error('Image upload failed')
  const { storageId } = await res.json()
  // Resolve the public URL
  const url = await convex.query(api.branches.getStorageUrl, { storageId })
  if (!url) throw new Error('Failed to resolve image URL')
  // Insert the image node
  const image: ImageElement = { type: 'image', url, alt: file.name, children: [{ text: '' }] }
  Transforms.insertNodes(editor, image)
  // Insert a paragraph after so the cursor has somewhere to go
  Transforms.insertNodes(editor, { type: 'paragraph', children: [{ text: '' }] })
}

// ---------------------------------------------------------------------------
// Toolbar buttons
// ---------------------------------------------------------------------------

interface MarkButtonProps { format: MarkFormat; label: string; title: string }

const MarkButton: React.FC<MarkButtonProps> = ({ format, label, title }) => {
  const editor = useSlate()
  const active = isMarkActive(editor, format)
  return (
    <button
      title={title}
      onMouseDown={(e) => { e.preventDefault(); toggleMark(editor, format) }}
      className={`px-2 py-1 rounded text-sm font-medium transition-colors ${
        active
          ? 'bg-primary-600 text-white'
          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
      }`}
    >
      {label}
    </button>
  )
}

interface BlockButtonProps { format: BlockFormat; label: string; title: string }

const BlockButton: React.FC<BlockButtonProps> = ({ format, label, title }) => {
  const editor = useSlate()
  const active = isBlockActive(editor, format)
  return (
    <button
      title={title}
      onMouseDown={(e) => { e.preventDefault(); toggleBlock(editor, format) }}
      className={`px-2 py-1 rounded text-sm font-medium transition-colors ${
        active
          ? 'bg-primary-600 text-white'
          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
      }`}
    >
      {label}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Element & Leaf renderers
// ---------------------------------------------------------------------------

const renderElement = (props: RenderElementProps) => {
  const { attributes, children, element } = props
  switch (element.type) {
    case 'heading-one':
      return <h1 {...attributes} className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-6 mb-2">{children}</h1>
    case 'heading-two':
      return <h2 {...attributes} className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-2">{children}</h2>
    case 'block-quote':
      return (
        <blockquote {...attributes} className="border-l-4 border-primary-400 pl-4 italic text-gray-600 dark:text-gray-400 my-3">
          {children}
        </blockquote>
      )
    case 'bulleted-list':
      return <ul {...attributes} className="list-disc list-inside my-2 space-y-1">{children}</ul>
    case 'list-item':
      return <li {...attributes} className="text-gray-800 dark:text-gray-200">{children}</li>
    case 'image':
      return <ImageBlock attributes={attributes} element={element}>{children}</ImageBlock>
    default:
      return <p {...attributes} className="text-gray-800 dark:text-gray-200 leading-relaxed my-1">{children}</p>
  }
}

// ---------------------------------------------------------------------------
// Image block component (shown in editor)
// ---------------------------------------------------------------------------

const ImageBlock: React.FC<{ attributes: RenderElementProps['attributes']; element: ImageElement; children: React.ReactNode }> = ({ attributes, element, children }) => {
  const selected = useSelected()
  const focused = useFocused()
  const editor = useSlate()
  return (
    <div {...attributes} contentEditable={false} className="my-4">
      <figure className="relative inline-block max-w-full">
        <img
          src={element.url}
          alt={element.alt ?? ''}
          className={`max-w-full max-h-[500px] mx-auto rounded-lg shadow-md transition-shadow ${
            selected && focused ? 'ring-2 ring-primary-500 shadow-lg' : ''
          }`}
          loading="lazy"
        />
        {selected && focused && (
          <button
            onClick={() => {
              const path = ReactEditor.findPath(editor, element)
              Transforms.removeNodes(editor, { at: path })
            }}
            className="absolute top-2 right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-700 shadow"
            title="Remove image"
          >
            ✕
          </button>
        )}
        {element.alt && (
          <figcaption className="mt-1 text-sm text-gray-500 dark:text-gray-400 italic text-center">
            {element.alt}
          </figcaption>
        )}
      </figure>
      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Image toolbar button
// ---------------------------------------------------------------------------

const ImageButton: React.FC = () => {
  const editor = useSlate()
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = React.useState(false)

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      await insertImage(editor, file)
    } catch {
      // silently fail — could add toast here
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  return (
    <>
      <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
      <button
        title="Insert illustration"
        onMouseDown={(e) => { e.preventDefault(); fileRef.current?.click() }}
        disabled={uploading}
        className={`px-2 py-1 rounded text-sm font-medium transition-colors ${
          uploading
            ? 'text-gray-400 cursor-wait'
            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
        }`}
      >
        {uploading ? '⏳' : '🖼'}
      </button>
    </>
  )
}

const renderLeaf = (props: RenderLeafProps) => {
  let { children } = props
  if (props.leaf.bold) children = <strong className="font-bold">{children}</strong>
  if (props.leaf.italic) children = <em className="italic">{children}</em>
  if (props.leaf.underline) children = <u className="underline">{children}</u>
  if (props.leaf.code) children = <code className="bg-gray-100 dark:bg-gray-800 rounded px-1 font-mono text-sm">{children}</code>
  return <span {...props.attributes}>{children}</span>
}

// ---------------------------------------------------------------------------
// Keyboard shortcuts
// ---------------------------------------------------------------------------

const onKeyDown = (event: React.KeyboardEvent, editor: Editor) => {
  if (!event.ctrlKey && !event.metaKey) return
  switch (event.key) {
    case 'b': event.preventDefault(); toggleMark(editor, 'bold'); break
    case 'i': event.preventDefault(); toggleMark(editor, 'italic'); break
    case 'u': event.preventDefault(); toggleMark(editor, 'underline'); break
    case '`': event.preventDefault(); toggleMark(editor, 'code'); break
  }
}

// ---------------------------------------------------------------------------
// Default empty content
// ---------------------------------------------------------------------------

export const EMPTY_SLATE_CONTENT: SlateContent = [
  { type: 'paragraph', children: [{ text: '' }] },
]

// ---------------------------------------------------------------------------
// Main RichTextEditor component
// ---------------------------------------------------------------------------

interface RichTextEditorProps {
  content: Descendant[]
  onChange: (value: Descendant[]) => void
  placeholder?: string
  readOnly?: boolean
  className?: string
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  placeholder = 'Begin your story…',
  readOnly = false,
  className = '',
}) => {
  const editor = useMemo(() => withImages(withReact(createEditor())), [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => { if (!readOnly) onKeyDown(e, editor) },
    [editor, readOnly],
  )

  return (
    <Slate editor={editor} initialValue={content} onChange={onChange}>
      {/* Toolbar */}
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-1 px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-t-lg">
          <span className="text-xs text-gray-400 mr-1 select-none">Format</span>
          <div className="w-px h-5 bg-gray-300 dark:bg-gray-600" />
          <MarkButton format="bold" label="B" title="Bold (Ctrl+B)" />
          <MarkButton format="italic" label="I" title="Italic (Ctrl+I)" />
          <MarkButton format="underline" label="U" title="Underline (Ctrl+U)" />
          <MarkButton format="code" label="<>" title="Code (Ctrl+`)" />
          <div className="w-px h-5 bg-gray-300 dark:bg-gray-600" />
          <BlockButton format="heading-one" label="H1" title="Heading 1" />
          <BlockButton format="heading-two" label="H2" title="Heading 2" />
          <BlockButton format="block-quote" label="❝" title="Block Quote" />
          <BlockButton format="bulleted-list" label="• List" title="Bulleted List" />
          <div className="w-px h-5 bg-gray-300 dark:bg-gray-600" />
          <ImageButton />
        </div>
      )}

      {/* Editable area */}
      <Editable
        className={`prose prose-gray dark:prose-invert max-w-none min-h-[400px] p-6 focus:outline-none text-gray-900 dark:text-gray-100 ${className}`}
        renderElement={renderElement}
        renderLeaf={renderLeaf}
        placeholder={placeholder}
        onKeyDown={handleKeyDown}
        readOnly={readOnly}
        spellCheck
      />
    </Slate>
  )
}

export default RichTextEditor
